import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DateInput, validateDateSubmission } from '../../../../shared/components/DateInput.js';

interface NamedRow {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly status: string;
}

interface FieldSpec {
  readonly name: string;
  readonly label: string;
  readonly type?: 'text' | 'date' | 'select';
  readonly required?: boolean;
  /** For a select: the config sub-path whose rows become the options. */
  readonly optionsFrom?: string;
}

interface CatalogSpec {
  readonly key: string;
  readonly title: string;
  readonly label: string; // aria-label for the table
  readonly path: string; // config sub-path
  readonly fields: readonly FieldSpec[];
  readonly build: (v: Record<string, string>) => Record<string, unknown>;
  /** These endpoints return entities (brandingTypeId/typeCode/typeName...),
   *  never id/code/name/status, so every catalog states its own mapping. */
  readonly map: (r: Record<string, unknown>) => NamedRow;
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** Every list is paginated and defaults to 20; the item catalogue alone already
 *  holds 25 rows, so the page would silently hide the rest. */
const PAGE_LIMIT = 500;

const CATALOGS: readonly CatalogSpec[] = [
  {
    key: 'branding-types',
    title: 'm17.configExtended.catBrandingTypes',
    label: 'branding-types-table',
    path: 'branding-types',
    fields: [
      { name: 'typeCode', label: 'm17.configExtended.fTypeCode', required: true },
      { name: 'typeName', label: 'm17.configExtended.fTypeName', required: true },
    ],
    build: (v) => ({ typeCode: v.typeCode, typeName: v.typeName }),
    map: (r) => ({
      id: str(r.brandingTypeId), code: str(r.typeCode),
      name: str(r.typeName), status: str(r.recordStatusCode),
    }),
  },
  {
    key: 'branding-items',
    title: 'm17.configExtended.catBrandingItems',
    label: 'branding-items-table',
    path: 'branding-items',
    fields: [
      {
        name: 'brandingTypeId', label: 'm17.configExtended.fBrandingType',
        type: 'select', optionsFrom: 'branding-types', required: true,
      },
      { name: 'itemCode', label: 'm17.configExtended.fItemCode', required: true },
      { name: 'itemName', label: 'm17.configExtended.fItemName', required: true },
    ],
    build: (v) => ({
      brandingTypeId: v.brandingTypeId, itemCode: v.itemCode, itemName: v.itemName,
    }),
    map: (r) => ({
      id: str(r.brandingItemId), code: str(r.itemCode),
      name: str(r.itemName), status: str(r.recordStatusCode),
    }),
  },
  {
    key: 'campaigns',
    title: 'm17.configExtended.catCampaigns',
    label: 'campaigns-table',
    path: 'campaigns',
    fields: [
      { name: 'campaignCode', label: 'm17.configExtended.fCampaignCode', required: true },
      { name: 'campaignName', label: 'm17.configExtended.fCampaignName', required: true },
      { name: 'startDate', label: 'm17.configExtended.fStartDate', type: 'date', required: true },
      { name: 'endDate', label: 'm17.configExtended.fEndDate', type: 'date', required: true },
    ],
    build: (v) => ({ campaignCode: v.campaignCode, campaignName: v.campaignName, startDate: v.startDate, endDate: v.endDate }),
    map: (r) => ({
      id: str(r.campaignId), code: str(r.campaignCode),
      name: str(r.campaignName), status: str(r.recordStatusCode),
    }),
  },
  {
    key: 'customer-categories',
    title: 'm17.configExtended.catCustomerCategories',
    label: 'customer-categories-table',
    path: 'customer-categories',
    fields: [
      { name: 'categoryCode', label: 'm17.configExtended.fCategoryCode', required: true },
      { name: 'categoryName', label: 'm17.configExtended.fCategoryName', required: true },
    ],
    build: (v) => ({ categoryCode: v.categoryCode, categoryName: v.categoryName }),
    map: (r) => ({
      id: str(r.customerCategoryId), code: str(r.categoryCode),
      name: str(r.categoryName), status: str(r.recordStatusCode),
    }),
  },
  {
    key: 'rejection-reasons',
    title: 'm17.configExtended.catRejectionReasons',
    label: 'rejection-reasons-table',
    path: 'rejection-reasons',
    fields: [
      { name: 'reasonCode', label: 'm17.configExtended.fReasonCode', required: true },
      { name: 'reasonText', label: 'm17.configExtended.fReasonText', required: true },
      { name: 'category', label: 'm17.configExtended.fCategory', required: true },
    ],
    build: (v) => ({ reasonCode: v.reasonCode, reasonText: v.reasonText, category: v.category }),
    map: (r) => ({
      id: str(r.rejectionReasonId), code: str(r.reasonCode),
      name: str(r.reasonText), status: str(r.recordStatusCode),
    }),
  },
];

function Catalog({ spec, token, onChanged }: { readonly spec: CatalogSpec; readonly token: string | null; readonly onChanged: () => void }): ReactNode {
  const { t } = useTranslation();
  const [rows, setRows] = useState<NamedRow[] | null>(null);
  const [options, setOptions] = useState<Record<string, readonly NamedRow[]>>({});
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setRows(null);
    apiRequest<{ data: Record<string, unknown>[] }>(
      `/v1/m17/config/${spec.path}?limit=${PAGE_LIMIT}`, { token },
    )
      .then((r) => { if (alive) setRows((r.data ?? []).map(spec.map)); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, spec.path, reloadKey]);

  // A select field is filled from another catalogue, e.g. an item's parent type.
  useEffect(() => {
    let alive = true;
    const sources = spec.fields
      .map((f) => f.optionsFrom)
      .filter((p): p is string => !!p);
    for (const src of sources) {
      const from = CATALOGS.find((c) => c.path === src);
      if (!from) continue;
      apiRequest<{ data: Record<string, unknown>[] }>(
        `/v1/m17/config/${src}?limit=${PAGE_LIMIT}`, { token },
      )
        .then((r) => {
          if (alive) setOptions((o) => ({ ...o, [src]: (r.data ?? []).map(from.map) }));
        })
        .catch((e) => { if (alive) setError((e as Error).message); });
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, spec.key, reloadKey]);

  async function onCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setNotice(null);
    for (const f of spec.fields) {
      if (f.required && !values[f.name]) { setError(t('m17.configExtended.fieldRequired', { label: t(f.label) })); return; }
    }
    setSaving(true);
    try {
      await apiRequest(`/v1/m17/config/${spec.path}`, { method: 'POST', token, body: spec.build(values) });
      setNotice(t('m17.configExtended.created', { title: t(spec.title) }));
      setValues({});
      setReloadKey((k) => k + 1);
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function onDeactivate(id: string): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/m17/config/${spec.path}/${id}`, { method: 'DELETE', token });
      setNotice(t('m17.configExtended.deactivated'));
      setReloadKey((k) => k + 1);
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <section>
      <h2>{t(spec.title)}</h2>
      <form onSubmit={onCreate} aria-label={`${spec.key}-create-form`} noValidate onSubmitCapture={validateDateSubmission}>
        {spec.fields.map((f) => (
          <span key={f.name}>
            <label htmlFor={`${spec.key}-${f.name}`}>{t(f.label)}</label>
            {f.type === 'select' ? (
              <select
                id={`${spec.key}-${f.name}`}
                value={values[f.name] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              >
                <option value="">{t('m17.configExtended.selectPrompt')}</option>
                {(options[f.optionsFrom ?? ''] ?? []).map((o) => (
                  <option key={o.id} value={o.id}>{o.name || o.code}</option>
                ))}
              </select>
            ) : f.type === 'date' ? (
              <DateInput
                id={`${spec.key}-${f.name}`}
                value={values[f.name] ?? ''}
                onValueChange={(nextValue) => setValues((v) => ({ ...v, [f.name]: nextValue }))}
              />
            ) : (
              <input
                id={`${spec.key}-${f.name}`}
                type="text"
                value={values[f.name] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              />
            )}
          </span>
        ))}
        <button type="submit" disabled={saving}>{t('m17.configExtended.addBtn')}</button>
      </form>
      {notice ? <p role="status">{notice}</p> : null}
      {error ? <p role="alert">{error}</p> : null}
      <table aria-label={spec.label}>
        <thead><tr><th>{t('m17.configExtended.colCode')}</th><th>{t('m17.configExtended.colName')}</th><th>{t('m17.configExtended.colStatus')}</th><th>{t('m17.configExtended.colActions')}</th></tr></thead>
        <tbody>
          {(rows ?? []).map((r) => (
            <tr key={r.id}>
              <td>{r.code}</td><td>{r.name}</td><td>{r.status}</td>
              <td><button type="button" aria-label={`deactivate-${r.code}`} onClick={() => onDeactivate(r.id)}>{t('m17.configExtended.deactivateBtn')}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** L17.05 — Extended configuration (`/admin/config-extended`). Full CRUD over
 *  the M17 master config catalogs (branding types and their items, campaigns,
 *  customer categories, rejection reasons). */
export function M17ConfigExtendedPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  return (
    <main aria-label="config-extended">
      <h1>{t('m17.configExtended.title')}</h1>
      {CATALOGS.map((c) => (
        <Catalog key={c.key} spec={c} token={token} onChanged={() => undefined} />
      ))}
    </main>
  );
}
